// CommandPalette — the generic ⌘K palette VIEW. A portal overlay (au-panel-in,
// slate palette, z-overlay; portaled to <body> so ancestor transforms can't clip
// it) carrying an autofocused search input over a filtered list of commands, with
// the two-step text-prompt flow, danger styling, arrow-nav, Esc/scrim close, and
// listbox a11y — all lifted verbatim from the process palette so the process
// adapter emits byte-identical DOM (its gate test is the guard). Surface-agnostic:
// the host owns open/close and passes the command list. `dataName` sets the root
// `data-{name}` attribute so an adapter can keep a stable test hook
// (e.g. "process-command-palette") while the global host uses "command-palette".
//
// Pure view: exports only components + a props/command type (Fast-Refresh safe).

import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { createPortal } from "react-dom";

/** A second-step prompt returned dynamically by `run` — the palette collects a
 *  line (placeholder = `label`) then calls `commit(value)` and closes. This is
 *  how a plugin command (whose `run(ctx)` decides at call time) opts into the
 *  type-a-name flow; process commands instead use the static `prompt` field. */
export interface PaletteRunPrompt {
  readonly label: string;
  readonly commit: (value: string) => void;
}

/** What `run` may return: `true` = applied (close), falsy = stay open, or a
 *  {@link PaletteRunPrompt} to enter the second-step prompt. */
export type PaletteRunResult = boolean | void | PaletteRunPrompt;

/** A single palette entry — the view-level command shape (today's ProcessCommand
 *  minus engine coupling). `run` returns true when the edit was applied (the
 *  palette closes on success), or a {@link PaletteRunPrompt} to enter the prompt;
 *  a static text-carrying command instead sets `prompt` up front and the palette
 *  collects a line then calls `runWithText`. */
export interface PaletteCommand {
  /** Stable id (test hook + React key). */
  readonly id: string;
  /** Human title shown + filtered on. */
  readonly title: string;
  /** Optional keyword aliases folded into the substring filter (never shown). */
  readonly keywords?: readonly string[];
  /** Optional group label (reserved for future grouping; not rendered yet). */
  readonly group?: string;
  /** A destructive action — styled danger (red) in the list. */
  readonly danger?: boolean;
  /** When set, running first prompts for a line (placeholder = `prompt.label`),
   *  then calls `runWithText(value)`. */
  readonly prompt?: { readonly label: string };
  /** Immediate command (no static `prompt`) — returns applied? or a prompt step. */
  readonly run?: () => PaletteRunResult;
  /** Text-carrying command — receives the prompted line. Present iff `prompt` set. */
  readonly runWithText?: (value: string) => boolean;
}

/** Case-insensitive substring filter over title + keywords. An empty query keeps
 *  every command (in registry order). */
export function filterPaletteCommands(
  cmds: readonly PaletteCommand[],
  query: string,
): readonly PaletteCommand[] {
  const q = query.trim().toLowerCase();
  if (q === "") return cmds;
  return cmds.filter((c) => {
    if (c.title.toLowerCase().includes(q)) return true;
    return (c.keywords ?? []).some((k) => k.toLowerCase().includes(q));
  });
}

export interface CommandPaletteProps {
  /** Whether the palette is shown (the host owns this, toggled by ⌘K). */
  readonly open: boolean;
  /** Close the palette (Esc / click-outside / after a committed run). */
  readonly onClose: () => void;
  /** The applicable commands, in list order. */
  readonly commands: readonly PaletteCommand[];
  /** Root data-attribute name (a stable test hook). Default "command-palette". */
  readonly dataName?: string;
}

/** The ⌘K palette. Renders nothing when closed. */
export function CommandPalette({ open, onClose, commands, dataName = "command-palette" }: CommandPaletteProps): ReactElement | null {
  if (!open) return null;
  return <PaletteOverlay onClose={onClose} commands={commands} dataName={dataName} />;
}

/** Mounted only while open — so `useState` (query / highlight / pending prompt)
 *  resets clean on every open, no stale carry-over. */
function PaletteOverlay({
  onClose,
  commands,
  dataName,
}: {
  readonly onClose: () => void;
  readonly commands: readonly PaletteCommand[];
  readonly dataName: string;
}): ReactElement | null {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  // A text-carrying command awaiting its line (second step). Null = the search
  // list is showing. Carries the prompt title/label + the commit that a non-empty
  // line runs — from a static `prompt`+`runWithText` (process) or a dynamic
  // `PaletteRunPrompt` returned by `run` (plugin).
  const [pending, setPending] = useState<{ title: string; label: string; commit: (v: string) => boolean } | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const visible = useMemo(() => filterPaletteCommands(commands, query), [commands, query]);

  // Keep the highlight inside the (shrinking/growing) filtered list.
  useEffect(() => {
    setActive((a) => (visible.length === 0 ? 0 : Math.min(a, visible.length - 1)));
  }, [visible.length]);

  const runCommand = (cmd: PaletteCommand | undefined) => {
    if (!cmd) return;
    if (cmd.prompt) {
      // Second step: collect a line, THEN run the static text command.
      setPending({ title: cmd.title, label: cmd.prompt.label, commit: (v) => cmd.runWithText?.(v) === true });
      return;
    }
    const r = cmd.run?.();
    if (r && typeof r === "object") {
      // A dynamic prompt step — enter the second step with its commit.
      setPending({ title: cmd.title, label: r.label, commit: (v) => { r.commit(v); return true; } });
      return;
    }
    if (r === true) onClose();
  };

  const commitPending = (value: string) => {
    if (!pending) return;
    // An empty line is never a stray edit — abandon the text step and return to
    // the command list (not stuck in the prompt).
    if (value.trim() === "") {
      setPending(null);
      return;
    }
    if (pending.commit(value) === true) onClose();
  };

  return createPortal(
    // Click-outside closes (a click on the scrim, not on the panel). No blur
    // dismissal here — focus stays trapped in the panel's inputs; blur-close is
    // handled implicitly by the scrim + Esc, matching the app's other overlays.
    <div
      {...{ [`data-${dataName}`]: "" }}
      className="au-overlay-in z-overlay fixed inset-0 flex items-start justify-center bg-black/40 p-4 pt-[12vh] backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="au-panel-in w-full max-w-lg overflow-hidden rounded-xl border border-slate-700 bg-slate-900/95 text-sm shadow-2xl shadow-black/50 backdrop-blur"
        // Swallow clicks on the panel so they don't hit the scrim's close.
        onMouseDown={(e) => e.stopPropagation()}
      >
        {pending ? (
          <PromptStep
            label={pending.label}
            title={pending.title}
            onCancel={() => setPending(null)}
            onCommit={commitPending}
          />
        ) : (
          <>
            <input
              ref={searchRef}
              autoFocus
              data-palette-search=""
              value={query}
              placeholder="Type a command…"
              aria-label="Command palette"
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setActive((a) => (visible.length === 0 ? 0 : (a + 1) % visible.length));
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setActive((a) => (visible.length === 0 ? 0 : (a - 1 + visible.length) % visible.length));
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  runCommand(visible[active]);
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();
                  onClose();
                }
              }}
              className="w-full border-b border-slate-700 bg-transparent px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none"
            />
            <ul data-palette-list="" className="max-h-80 overflow-y-auto py-1" role="listbox">
              {visible.length === 0 ? (
                <li className="px-4 py-2 text-slate-500">No matching commands</li>
              ) : (
                visible.map((cmd, i) => (
                  <li key={cmd.id} role="option" aria-selected={i === active}>
                    <button
                      type="button"
                      data-palette-item={cmd.id}
                      data-active={i === active ? "" : undefined}
                      // Mouse hover moves the highlight so pointer + keyboard agree.
                      onMouseEnter={() => setActive(i)}
                      // mousedown (not click) so the scrim's outside-close never
                      // pre-empts the run when focus shifts.
                      onMouseDown={(e) => {
                        e.preventDefault();
                        runCommand(cmd);
                      }}
                      className={
                        "flex w-full items-center gap-2 px-4 py-1.5 text-left text-[13px] outline-none " +
                        (cmd.danger
                          ? i === active
                            ? "bg-red-500/10 text-red-300"
                            : "text-red-400 hover:bg-red-500/10 hover:text-red-300"
                          : i === active
                            ? "bg-slate-800 text-slate-100"
                            : "text-slate-300 hover:bg-slate-800 hover:text-slate-100")
                      }
                    >
                      {cmd.title}
                    </button>
                  </li>
                ))
              )}
            </ul>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** The second step for a text-carrying command: an autofocused inline input.
 *  Enter commits the line, Escape returns to the command list. */
function PromptStep({
  label,
  title,
  onCommit,
  onCancel,
}: {
  readonly label: string;
  readonly title: string;
  readonly onCommit: (value: string) => void;
  readonly onCancel: () => void;
}): ReactElement {
  const [value, setValue] = useState("");
  return (
    <div className="px-4 py-3">
      <div className="mb-2 text-[11px] uppercase tracking-wide text-slate-500">{title}</div>
      <input
        autoFocus
        data-palette-prompt=""
        value={value}
        placeholder={label}
        aria-label={label}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onCommit(value);
          } else if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          }
        }}
        className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100 placeholder:text-slate-500 outline-none focus:border-sky-500"
      />
    </div>
  );
}
